import React from 'react';

import { History } from 'history';
import { observer } from 'mobx-react';
import { Route, RouteComponentProps, Router, Switch } from 'react-router-dom';

import { BasePath, PagePath, getOrderedBasePaths, pagePathToPathString } from 'services/base/page_path';
import { AccountsDomainProvider, GlobalAccountsDomain } from 'services/accounts/accounts_domain';
import { GlobalPangolinDomains, PangolinDomainsContext } from 'services/pangolin/pangolin_domain';

import { DefaultToaster } from 'ui/base/toast';
import { ThemeProvider, GlobalThemeStore } from 'ui/base/theme';

import { ArmadilloLoader } from 'pages/loader/armadillo_loader/armadillo_loader';

import { Introduction } from 'pages/public/introduction/introduction';
import { Register } from 'pages/public/register/register';
import { Login } from 'pages/public/login/login';
import { PasswordReset } from 'pages/public/password_reset/password_reset';
import { PasswordResetConfirmation } from 'pages/public/password_reset_confirmation/password_reset_confirmation';

import { Home } from 'pages/core/home/home';
import { More } from 'pages/core/more/more';
import { Redirect } from 'pages/core/redirect/redirect';

import { Settings } from 'pages/accounts/settings/settings';
import { EditSettings } from 'pages/accounts/edit_settings/edit_settings';
import { ChangePassword } from 'pages/accounts/change_password/change_password';
import { DeleteAccount } from 'pages/accounts/delete_account/delete_account';
import { EmailVerification } from 'pages/accounts/email_verification/email_verification';

import { AddPangolinSetup } from 'pages/add_pangolin/add_pangolin_setup/add_pangolin_setup';
import { AddPangolinPowerUp } from 'pages/add_pangolin/add_pangolin_power_up/add_pangolin_power_up';
import { AddPangolinMethod } from 'pages/add_pangolin/add_pangolin_method/add_pangolin_method';
import { AddPangolinMnemonic } from 'pages/add_pangolin/add_pangolin_mnemonic/add_pangolin_mnemonic';
import { AddPangolinRename } from 'pages/add_pangolin/add_pangolin_rename/add_pangolin_rename';
import { AddPangolinProfile } from 'pages/add_pangolin/add_pangolin_profile/add_pangolin_profile';
import { AddPangolinSuccess } from 'pages/add_pangolin/add_pangolin_success/add_pangolin_success';

import { ManagePangolinsList } from 'pages/pangolins/manage_pangolins_list/manage_pangolins_list';
import { ViewPangolin } from 'pages/pangolins/view_pangolin/view_pangolin';
import { EditPangolin } from 'pages/pangolins/edit_pangolin/edit_pangolin';
import { ManagePlans } from 'pages/pangolins/manage_plans/manage_plans';
import { ManageUpdates } from 'pages/pangolins/manage_updates/manage_updates';
import { InterceptTuning } from 'pages/pangolins/intercept_tuning/intercept_tuning';
import { ScannerTuning } from 'pages/pangolins/scanner_tuning/scanner_tuning';
import { UnlinkPangolin } from 'pages/pangolins/unlink_pangolin/unlink_pangolin';

import { ListDevices } from 'pages/devices/list_devices/list_devices';
import { ViewDevice } from 'pages/devices/view_device/view_device';
import { EditDevice } from 'pages/devices/edit_device/edit_device';
import { DeviceInformation } from 'pages/devices/device_information/device_information';

import { ListIssues } from 'pages/issues/list_issues/list_issues';

import { ListHistory } from 'pages/history/list_history/list_history';

import { ListSchedules } from 'pages/schedules/list_schedules/list_schedules';
import { CreateSchedule } from 'pages/schedules/edit_schedule/create_schedule';
import { ViewSchedule } from 'pages/schedules/view_schedule/view_schedule';
import { EditSchedule } from 'pages/schedules/edit_schedule/edit_schedule';

import { BandwidthPrioritization } from 'pages/bandwidth/bandwidth_prioritization/bandwidth_prioritization';
import { BandwidthSettings } from 'pages/bandwidth/bandwidth_settings/bandwidth_settings';

import { ListVPNClientsList } from 'pages/vpn_clients/list_vpn_clients/list_vpn_clients';

import { EditVPNClient } from 'pages/vpn_clients/edit_vpn_client/edit_vpn_client';
import { CreateVPNClient } from 'pages/vpn_clients/edit_vpn_client/create_vpn_client';

import './application.css';

type RouteConfig<T extends object> = {
  component: React.ComponentType<RouteComponentProps<T>>;
  pagePath: PagePath;
  exact: boolean;
};

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
const routeConfigs: Record<BasePath, RouteConfig<any>> = {
  [BasePath.INTRO]: { component: Introduction, pagePath: { kind: BasePath.INTRO }, exact: true },
  [BasePath.REGISTER]: { component: Register, pagePath: { kind: BasePath.REGISTER }, exact: true },
  [BasePath.LOGIN]: { component: Login, pagePath: { kind: BasePath.LOGIN }, exact: true },
  [BasePath.PASSWORD_RESET]: { component: PasswordReset, pagePath: { kind: BasePath.PASSWORD_RESET }, exact: true },
  [BasePath.PASSWORD_RESET_CONFIRMATION]: { component: PasswordResetConfirmation, pagePath: { kind: BasePath.PASSWORD_RESET_CONFIRMATION }, exact: true },
  [BasePath.EMAIL_VERIFICATION]: { component: EmailVerification, pagePath: { kind: BasePath.EMAIL_VERIFICATION }, exact: true },
  [BasePath.ADD_PANGOLIN_SETUP]: { component: AddPangolinSetup, pagePath: { kind: BasePath.ADD_PANGOLIN_SETUP }, exact: true },
  [BasePath.ADD_PANGOLIN_POWER]: { component: AddPangolinPowerUp, pagePath: { kind: BasePath.ADD_PANGOLIN_POWER }, exact: true },
  [BasePath.ADD_PANGOLIN_METHOD]: { component: AddPangolinMethod, pagePath: { kind: BasePath.ADD_PANGOLIN_METHOD }, exact: true },
  [BasePath.ADD_PANGOLIN_MNEMONIC]: { component: AddPangolinMnemonic, pagePath: { kind: BasePath.ADD_PANGOLIN_MNEMONIC }, exact: true },
  [BasePath.ADD_PANGOLIN_RENAME]: { component: AddPangolinRename, pagePath: { kind: BasePath.ADD_PANGOLIN_RENAME, pangolinID: ':pangolinID' }, exact: true },
  [BasePath.ADD_PANGOLIN_PROFILE]: { component: AddPangolinProfile, pagePath: { kind: BasePath.ADD_PANGOLIN_PROFILE, pangolinID: ':pangolinID' }, exact: true },
  [BasePath.ADD_PANGOLIN_SUCCESS]: { component: AddPangolinSuccess, pagePath: { kind: BasePath.ADD_PANGOLIN_SUCCESS, pangolinID: ':pangolinID' }, exact: true },
  [BasePath.HOME]: { component: Home, pagePath: { kind: BasePath.HOME, pangolinID: ':pangolinID' }, exact: true },
  [BasePath.PLANS]: { component: ManagePlans, pagePath: { kind: BasePath.PLANS, pangolinID: ':pangolinID' }, exact: true },
  [BasePath.UPDATES]: { component: ManageUpdates, pagePath: { kind: BasePath.UPDATES, pangolinID: ':pangolinID' }, exact: true },
  [BasePath.DEVICES_LIST]: { component: ListDevices, pagePath: { kind: BasePath.DEVICES_LIST, pangolinID: ':pangolinID' }, exact: true },
  [BasePath.DEVICES_INFO]: { component: DeviceInformation, pagePath: { kind: BasePath.DEVICES_INFO, pangolinID: ':pangolinID' }, exact: true },
  [BasePath.DEVICES_VIEW]: { component: ViewDevice, pagePath: { kind: BasePath.DEVICES_VIEW, pangolinID: ':pangolinID', deviceID: ':deviceID' }, exact: true },
  [BasePath.DEVICES_EDIT]: { component: EditDevice, pagePath: { kind: BasePath.DEVICES_EDIT, pangolinID: ':pangolinID', deviceID: ':deviceID' }, exact: true },
  [BasePath.LIST_ISSUES]: { component: ListIssues, pagePath: { kind: BasePath.LIST_ISSUES, pangolinID: ':pangolinID' }, exact: false },
  [BasePath.MORE]: { component: More, pagePath: { kind: BasePath.MORE, pangolinID: ':pangolinID' }, exact: true },
  [BasePath.HISTORY_LIST]: { component: ListHistory, pagePath: { kind: BasePath.HISTORY_LIST, pangolinID: ':pangolinID' }, exact: false },
  [BasePath.BANDWIDTH_LIST]: { component: BandwidthPrioritization, pagePath: { kind: BasePath.BANDWIDTH_LIST, pangolinID: ':pangolinID' }, exact: true },
  [BasePath.BANDWIDTH_SETTINGS]: { component: BandwidthSettings, pagePath: { kind: BasePath.BANDWIDTH_SETTINGS, pangolinID: ':pangolinID' }, exact: true },
  [BasePath.SCHEDULE_LIST]: { component: ListSchedules, pagePath: { kind: BasePath.SCHEDULE_LIST, pangolinID: ':pangolinID' }, exact: true },
  [BasePath.SCHEDULE_ADD]: { component: CreateSchedule, pagePath: { kind: BasePath.SCHEDULE_ADD, pangolinID: ':pangolinID' }, exact: true },
  [BasePath.SCHEDULE_VIEW]: { component: ViewSchedule, pagePath: { kind: BasePath.SCHEDULE_VIEW, pangolinID: ':pangolinID', scheduleID: ':scheduleID' }, exact: true },
  [BasePath.SCHEDULE_EDIT]: { component: EditSchedule, pagePath: { kind: BasePath.SCHEDULE_EDIT, pangolinID: ':pangolinID', scheduleID: ':scheduleID' }, exact: true },
  [BasePath.VPN_LIST]: { component: ListVPNClientsList, pagePath: { kind: BasePath.VPN_LIST, pangolinID: ':pangolinID' }, exact: true },
  [BasePath.VPN_ADD]: { component: CreateVPNClient, pagePath: { kind: BasePath.VPN_ADD, pangolinID: ':pangolinID' }, exact: true },
  [BasePath.VPN_EDIT]: { component: EditVPNClient, pagePath: { kind: BasePath.VPN_EDIT, pangolinID: ':pangolinID', clientID: ':clientID' }, exact: true },
  [BasePath.MANAGE_PANGOLIN_LIST]: { component: ManagePangolinsList, pagePath: { kind: BasePath.MANAGE_PANGOLIN_LIST }, exact: true },
  [BasePath.MANAGE_PANGOLIN_VIEW]: { component: ViewPangolin, pagePath: { kind: BasePath.MANAGE_PANGOLIN_VIEW, pangolinID: ':pangolinID' }, exact: true },
  [BasePath.MANAGE_PANGOLIN_EDIT]: { component: EditPangolin, pagePath: { kind: BasePath.MANAGE_PANGOLIN_EDIT, pangolinID: ':pangolinID' }, exact: true },
  [BasePath.MANAGE_PANGOLIN_INTERCEPT]: { component: InterceptTuning, pagePath: { kind: BasePath.MANAGE_PANGOLIN_INTERCEPT, pangolinID: ':pangolinID' }, exact: true },
  [BasePath.MANAGE_PANGOLIN_SCANNER]: { component: ScannerTuning, pagePath: { kind: BasePath.MANAGE_PANGOLIN_SCANNER, pangolinID: ':pangolinID' }, exact: true },
  [BasePath.MANAGE_PANGOLIN_UNLINK]: { component: UnlinkPangolin, pagePath: { kind: BasePath.MANAGE_PANGOLIN_UNLINK, pangolinID: ':pangolinID' }, exact: true },
  [BasePath.SETTINGS]: { component: Settings, pagePath: { kind: BasePath.SETTINGS }, exact: true },
  [BasePath.SETTINGS_EDIT]: { component: EditSettings, pagePath: { kind: BasePath.SETTINGS_EDIT }, exact: true },
  [BasePath.SETTINGS_PASSWORD_CHANGE]: { component: ChangePassword, pagePath: { kind: BasePath.SETTINGS_PASSWORD_CHANGE }, exact: true },
  [BasePath.SETTINGS_ACCOUNT_DELETE]: { component: DeleteAccount, pagePath: { kind: BasePath.SETTINGS_ACCOUNT_DELETE }, exact: true },
};

export function createApplication(history: History) {
  const Application: React.FC = observer(() => {
    return (
      <ThemeProvider value={GlobalThemeStore.get()}>
        <AccountsDomainProvider value={GlobalAccountsDomain}>
          <PangolinDomainsContext.Provider value={GlobalPangolinDomains}>
            <Router history={history}>
              <ArmadilloLoader>
                <Switch>
                  {getOrderedBasePaths().map((bp: BasePath) => {
                    const config = routeConfigs[bp];
                    return (
                      <Route
                        key={bp}
                        path={pagePathToPathString(config.pagePath)}
                        component={config.component}
                        exact={config.exact}
                      />
                    );
                  })}
                  <Route component={Redirect}/>
                </Switch>
              </ArmadilloLoader>
            </Router>
            <DefaultToaster />
          </PangolinDomainsContext.Provider>
        </AccountsDomainProvider>
      </ThemeProvider>
    );
  });
  return Application;
}
